import { useEffect, useState, useMemo } from 'react';
import { useOrgUserStore } from '../../../shared/organization/orgUserStore';
import Card from './card/Card';
import CardBody from './card/CardBody';
import CardHeader from './card/CardHeader';
import CardList from './card/CardList';
import CardListItem from './card/CardListItem';
import {
  deleteOrgUserInviteAction,
  fetchOrganization,
  getOrgMembersAction,
  getOrgUserInvitesAction,
  updateOrgMembersActions,
} from '../../../shared/organization/organizationActions';
import ItemMenu from '../../../components/ItemMenu/ItemMenu';
import ItemMenuListItem from '../../../components/ItemMenu/ItemMenuListItem';
import { Button } from '../../../components/Form/Button';
import { useAuthStore } from '../../auth/authStore';
import { dashboardPermissionCheck } from '../store/dashboardActions';
import { openConfirmationModal } from '../../../shared/components/modal/openConfirmationModal';
import { PlusIcon } from '@heroicons/react/24/outline';
import InviteForm from '../../auth/components/InviteForm';
import { useModalStore } from '../../../shared/store/uiStore';
import CardFooter from './card/CardFooter';
import {
  Organization,
  OrganizationUser,
} from '../../../shared/organization/types';
import { useOrganizationStore } from '../../../shared/organization/organizationStore';
import { functionsCreateCustomerPortalSession } from '../../../shared/services/firestoreUtilities';
import UserSelect from '../../billing/components/UserSelect';
import { useOrgUserInviteStore } from '../../../shared/organization/orgUserInviteStore';

const Badge = () => {
  return (
    <span
      className={` ml-2 inline-flex items-center rounded-md  px-1.5 py-0.5 text-xs font-medium  ring-1  ring-inset bg-blue-50 text-blue-700 ring-blue-700/10`}
    >
      Self
    </span>
  );
};

const UsersAvailable = ({
  results,
  invites,
  userLimit,
}: {
  results: number;
  invites: number;
  userLimit: number;
}) => {
  return userLimit - results + invites > 0 ? (
    <span className="text-xs text-gray-500">
      {results} / {userLimit} Users{' '}
      {invites > 0 && (
        <span className="text-xs text-gray-500">{invites} Invites sent</span>
      )}
    </span>
  ) : (
    <span className="text-xs text-red-500">User limit reached</span>
  );
};

const HeaderButton = ({
  users,
  userLimit,
  canEdit,
  org,
}: {
  users: number;
  userLimit: number;
  canEdit: boolean;
  org: Organization;
}) => {
  const openModal = useModalStore((state) => state.openModal);
  const handleInvite = () => {
    openModal({
      id: 'invite-modal',
      title: 'Invite User',
      size: 'md',
      content: <InviteForm />,
      buttons: [
        {
          label: 'Invite',
          form: 'invite-form',
          type: 'submit',
          variant: 'primary',
          autoFocus: true,
          showLoading: true,
        },
        {
          label: 'Cancel',
          onClick: () => useModalStore.getState().closeModal(),
          type: 'button',
          variant: 'outline',
          autoFocus: false,
        },
      ],
    });
  };

  const handleClick = async () => {
    if (org?.stripeCustomerId && org?.stripeSubscriptionId) {
      await functionsCreateCustomerPortalSession(org.stripeCustomerId);
    } else {
      openModal({
        title: 'Upgrade Subscription',
        content: <UserSelect />,
        id: 'upgrade-subscription',
        size: 'sm',
      });
    }
  };

  if (canEdit && users >= userLimit) {
    return (
      <Button
        type="button"
        variant="outline"
        className="flex items-center"
        onClick={handleClick}
      >
        <span className="text-xs">
          {org?.stripeCustomerId ? 'Upgrade Subscription' : 'Add Subscription'}
        </span>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      data-testid="invite-user-button"
      variant="primary"
      onClick={handleInvite}
      disabled={!canEdit || users >= userLimit}
      className="flex items-center"
    >
      <PlusIcon className="size-5" aria-hidden="true" />
      <span className="ml-1">Invite User</span>
    </Button>
  );
};

const UserCard = () => {
  const users = useOrgUserStore((state) => state.items);
  const invites = useOrgUserInviteStore((state) => state.items);
  const editorCount = useOrganizationStore(
    (state) => state.organization?.editorCount,
  );
  const auth = useAuthStore((state) => state.auth);
  const org = useOrganizationStore((state) => state.organization);
  const [page, setPage] = useState<Array<{ [key: string]: string | Date }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const userLimit = org?.userLimit || 0;
  const closeModal = useModalStore((state) => state.closeModal);
  const openModal = useModalStore((state) => state.openModal);

  const results = useMemo(
    () => [
      ...Object.values(users).map((user) => ({
        ...user,
        // If this is the current user, use the latest displayName from currentUser
        displayName: user.displayName,
        type: 'user',
        id: user.uid,
        role: user.role,
      })),
      ...Object.values(invites).map((invite) => {
        return {
          ...invite,
          type: 'invite',
          id: invite.id,
          displayName: invite.displayName,
        };
      }),
    ],
    [users, invites],
  );

  const totalResults = results.length;
  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => {
    getOrgMembersAction();
    getOrgUserInvitesAction();
    if (!org && auth?.memberships[0].organizationId) {
      fetchOrganization(auth?.memberships[0].organizationId);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!users) return;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPage(results.slice(startIndex, endIndex));
  }, [results, users, currentPage]);

  useEffect(() => {
    if (!auth) return;
    if (!users) return;
    const canEdit = dashboardPermissionCheck('admin', auth.uid, users);
    setCanEdit(canEdit);
  }, [users, auth]);

  const handleDelete = (
    displayName: string,
    uid: string,
    type: 'invite' | 'user',
  ) => {
    if (!canEdit) return;
    openConfirmationModal({
      message: `Are you sure you want to delete the ${
        type === 'invite' ? 'invite for' : 'account of'
      } ${displayName}?`,
      onConfirm: async () => {
        if (type === 'invite') {
          await deleteOrgUserInviteAction(uid);
        } else {
          await updateOrgMembersActions([{ type: 'delete', uid }]);
        }
        closeModal();
      },
      variant: 'warning',
    });
  };

  if (!org) {
    return null;
  }

  const handleNext = () => {
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPage(results.slice(startIndex, endIndex));
    setCurrentPage(nextPage);
  };

  const handlePrevious = () => {
    const previousPage = currentPage - 1;
    const startIndex = (previousPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPage(results.slice(startIndex, endIndex));
    setCurrentPage(previousPage);
  };

  return (
    <Card className="col-span-1 row-span-2">
      <CardHeader>
        <div className="flex flex-col w-full ml-1">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center mt-2">
              <h3 className="text-base font-semibold text-gray-900">Users</h3>
              <p className="text-xs text-gray-500 ml-2">
                <UsersAvailable
                  results={editorCount || Object.keys(users).length}
                  invites={Object.keys(invites).length}
                  userLimit={userLimit}
                />
              </p>
            </div>
            <div className="ml-4 mt-2 shrink-0">
              <HeaderButton
                users={Object.keys(users).length}
                userLimit={userLimit}
                canEdit={canEdit}
                org={org}
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <CardList>
          {page.map((result) => (
            <CardListItem key={result.id as string}>
              <div className="min-w-0">
                <div className="flex items-center gap-x-3">
                  <h3
                    className={`text-sm font-semibold leading-6 ${
                      result.type === 'invite'
                        ? 'text-gray-400'
                        : 'text-gray-900'
                    }`}
                  >
                    {result.displayName as string}
                    {result.uid === auth?.uid && <Badge />}
                    {result.type === 'invite' && (
                      <span className="text-xs text-gray-500 ml-2">
                        Pending
                      </span>
                    )}
                  </h3>
                </div>
              </div>
              <div className="flex flex-none items-center gap-x-4">
                {result.type === 'invite' ? (
                  <ItemMenu>
                    <ItemMenuListItem
                      label="Delete"
                      as={Button}
                      onClick={() => {
                        handleDelete(
                          result.displayName as string,
                          result.id as string,
                          result.type as 'invite' | 'user',
                        );
                      }}
                      disabled={result.uid === auth?.uid || !canEdit}
                    />
                  </ItemMenu>
                ) : result?.role !== 'owner' ? (
                  <ItemMenu>
                    <ItemMenuListItem
                      label="Edit"
                      as={Button}
                      onClick={() => {
                        openModal({
                          id: 'edit-user-modal',
                          title: 'Edit User Role',
                          size: 'md',
                          content: (
                            <InviteForm
                              user={result as unknown as OrganizationUser}
                            />
                          ),
                          buttons: [
                            {
                              label: 'Save',
                              form: 'invite-form',
                              type: 'submit',
                              variant: 'primary',
                              autoFocus: true,
                              showLoading: true,
                            },
                            {
                              label: 'Cancel',
                              onClick: () =>
                                useModalStore.getState().closeModal(),
                              type: 'button',
                              variant: 'outline',
                              autoFocus: false,
                            },
                          ],
                        });
                      }}
                      disabled={!canEdit}
                    />
                    <ItemMenuListItem
                      label="Delete"
                      as={Button}
                      onClick={() => {
                        handleDelete(
                          result.displayName as string,
                          result.id as string,
                          result.type as 'invite' | 'user',
                        );
                      }}
                      disabled={result.uid === auth?.uid || !canEdit}
                    />
                  </ItemMenu>
                ) : null}
              </div>
            </CardListItem>
          ))}
        </CardList>
      </CardBody>
      {totalResults >= pageSize && (
        <CardFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          currentPage={currentPage}
          pageSize={pageSize}
          totalResults={totalResults}
        />
      )}
    </Card>
  );
};
export default UserCard;
